// AI Focus — daily 8-block time plan + top priorities + pep talk (Gemini, once per day).
import { useEffect, useState } from "react";

import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Check, Loader2, Target, ArrowUpRight, Flame } from "lucide-react";
import FocusTeam from "./FocusTeam";

const API = process.env.REACT_APP_BACKEND_URL + "/api/me/focus";
const LANE = { sales: "border-l-orange-400 text-orange-300", company: "border-l-sky-400 text-sky-300", admin: "border-l-slate-400 text-slate-300", break: "border-l-emerald-400 text-emerald-300" };

function useOpenItem(onOverlay, onOpenTask) {

  return (it) => {
    if (!it) return;
    if (["task", "checkin", "pool"].includes(it.kind)) onOpenTask?.(it.id);
    else if (it.kind === "quote") window.dispatchEvent(new CustomEvent("dash:open-quote", { detail: { id: it.id, person_id: it.person_id } }));
    else if (it.person_id) onOverlay?.(it.person_id);
  };
}

function Block({ b, done, onTick, onOpen, i }) {
  return (
    <div className={`flex items-start gap-3 pl-3 pr-2 py-2 border-l-2 rounded-r-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors focus-in ${LANE[b.lane] || LANE.company} ${done ? "opacity-50" : ""}`}
      style={{ animationDelay: `${i * 45}ms` }} data-testid={`focus-block-${b.id}`}>
      <span className="font-mono text-[11px] text-slate-400 w-[86px] shrink-0 pt-0.5 tabular-nums">{b.start}–{b.end}</span>
      <div className="min-w-0 flex-1">
        <div className={`text-[13.5px] text-white ${done ? "line-through" : ""}`}>{b.title}</div>
        {b.why && <div className="text-[11.5px] text-slate-400 mt-0.5">{b.why}</div>}
      </div>
      <span className="text-[10px] uppercase tracking-wider font-mono pt-1 hidden sm:block">{b.lane}</span>
      {b.item && <button onClick={() => onOpen(b.item)} className="p-1 rounded border border-white/10 hover:border-white/30 text-slate-300" title="Open" data-testid={`focus-open-${b.id}`}><ArrowUpRight className="w-3.5 h-3.5" /></button>}
      <button onClick={() => onTick(b.id, !done)} title={done ? "Mark not done" : "Mark done"} data-testid={`focus-tick-${b.id}`}
        className={`p-1 rounded border transition-colors ${done ? "bg-teal-500/20 border-teal-400/60 text-teal-200" : "border-white/10 text-slate-400 hover:border-teal-400/60"}`}><Check className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function Streak({ st, done, total }) {
  const need = total ? Math.ceil(total * 0.75) : 6;
  const left = total ? Math.max(0, need - done) : null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11.5px]" data-testid="focus-streak">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${st.streak ? "border-amber-400/50 bg-amber-400/10 text-amber-200" : "border-white/10 text-slate-400"}`} data-testid="focus-streak-count">
        <Flame className="w-3 h-3" />{st.streak || 0}-day streak</span>
      <span className="text-slate-400" data-testid="focus-week-hits">{st.week_hits || 0} focus day{st.week_hits === 1 ? "" : "s"} this week</span>
      {left !== null && <span className="text-slate-500" data-testid="focus-streak-left">{left ? `${left} more to count today` : "Today counts!"}</span>}
    </div>
  );
}

function MyFocus({ onOverlay, onOpenTask }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const open = useOpenItem(onOverlay, onOpenTask);
  useEffect(() => { axios.get(API, { withCredentials: true }).then((r) => setSt(r.data)).catch(() => setSt({ plan: null, can_generate: false })); }, []);
  const gen = async () => {
    setBusy(true);
    try { setSt((await axios.post(API, { note }, { withCredentials: true })).data); toast.success("Your Focus plan is ready"); }
    catch (e) { toast.error(e.response?.data?.detail || "AI Focus failed"); }
    finally { setBusy(false); }
  };
  const tick = async (id, done) => { try { setSt((await axios.post(`${API}/done`, { block_id: id, done }, { withCredentials: true })).data); } catch { toast.error("Couldn't update"); } };
  if (!st) return <div className="px-4 py-5 text-[12px] text-slate-500 flex items-center gap-2" data-testid="focus-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading Focus…</div>;
  const p = st.plan;
  if (!p) return (
    <div className="px-4 py-4 border-t border-white/[0.05] space-y-3" data-testid="focus-empty">
      <Streak st={st} />
      <p className="text-[12.5px] text-slate-300">AI looks across your activities, quotes and tasks and builds today's plan: 8 time blocks, your top priorities and a quick pep talk.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything on your mind today? (optional — e.g. off at 3pm, chase big quotes)"
          className="flex-1 bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-[12.5px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-teal-400/60" data-testid="focus-note-input" />
        <button onClick={gen} disabled={busy || !st.can_generate} data-testid="focus-generate-btn"
          className="px-4 py-2 rounded-lg bg-teal-500/20 border border-teal-400/50 text-teal-100 text-[12.5px] inline-flex items-center justify-center gap-2 hover:bg-teal-500/30 disabled:opacity-50 transition-colors">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}{busy ? "Planning your day…" : "Build my day"}</button>
      </div>
      {!st.can_generate && <p className="text-[11px] text-amber-300" data-testid="focus-limit-msg">You've used today's Focus — it refreshes tomorrow.</p>}
    </div>
  );
  const done = new Set(p.done || []);
  return (
    <div className="px-4 py-4 border-t border-white/[0.05] space-y-4" data-testid="focus-plan">
      <Streak st={st} done={done.size} total={p.blocks.length} />
      {p.pep_talk && <div className="rounded-lg border border-amber-400/25 bg-amber-400/[0.05] px-3.5 py-2.5 text-[13px] text-amber-50 leading-relaxed focus-in" data-testid="focus-pep-talk"><Sparkles className="w-3.5 h-3.5 inline mr-1.5 text-amber-300 -mt-0.5" />{p.pep_talk}</div>}
      {(p.priorities || []).length > 0 && <div className="flex flex-wrap items-center gap-1.5" data-testid="focus-priorities">
        <span className="text-[10px] uppercase tracking-wider font-mono text-slate-400 mr-1 inline-flex items-center gap-1"><Target className="w-3 h-3" />Top priorities</span>
        {p.priorities.map((x, i) => <button key={x.key} onClick={() => open(x.item)} title={x.why} data-testid={`focus-priority-${i}`}
          className="px-2.5 py-1 rounded-full border border-teal-400/30 bg-teal-500/[0.07] text-[11.5px] text-teal-100 hover:border-teal-300/70 truncate max-w-[280px] transition-colors">{i + 1}. {x.item?.title}</button>)}
      </div>}
      <div className="space-y-1.5" data-testid="focus-blocks">
        {p.blocks.map((b, i) => <Block key={b.id} b={b} i={i} done={done.has(b.id)} onTick={tick} onOpen={open} />)}
      </div>
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span data-testid="focus-progress">{done.size}/{p.blocks.length} done</span>
        {st.can_generate && <button onClick={gen} disabled={busy} className="text-teal-300 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="focus-rebuild-btn">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}Rebuild ({st.daily_limit - st.runs} left today)</button>}
        <span>Built {new Date(p.generated_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} · refreshes tomorrow</span>
      </div>
    </div>
  );
}

export default function FocusPanel(props) {
  const [view, setView] = useState("me");
  return (
    <div data-testid="focus-panel">
      <div className="flex items-center gap-1 px-4 pb-2">
        {[["me", "My day"], ["team", "Team"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`focus-view-${k}`}
            className={`px-2.5 py-0.5 rounded-md text-[11px] border transition-colors ${view === k ? "border-amber-300/50 bg-amber-400/10 text-amber-100" : "border-transparent text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {view === "me" ? <MyFocus {...props} /> : <FocusTeam />}
    </div>
  );
}
